import { FullPageCard } from 'components/layout/FullPageCard/FullPageCard';
import styled from 'styled-components';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { FormikHelpers } from 'formik';
import { Page } from 'components/layout/Page/Page';
import { SplitPageLayout } from 'components/layout/SplitPageLayout/SplitPageLayout';
import { useAppUserLoginMutation } from 'hooks/api/user/mutations/auth/useAppUserLogin';
import { useTheme } from '@mui/styles';
import {
  dvListingPage,
  signUpPageFeeScreenHostImage,
  signUpPageFeeScreenGuestImage,
  dvLogo,
  logoInvertV2,
} from 'config/imageUrls';
import { LoginFormikForm } from 'components/views/pages/login/LoginFormikForm';
import { handleTransition, userNextRouteTransitions } from 'hooks/useNextRouteTransitions';
import { User, useUserQuery } from '@alluradirect/react';
import { useIsUserLoggedInRedirectToManagement } from '../signup/hooks/useIsUserLoggedInRedirectToManagment';
import { IS_DIRECT_VACATIONS } from 'config/envSetup';
import { AppLink } from 'components/core/AppLink/AppLink';
import { httpsUrlTrailingSlash } from 'config/url';

const StyledBox = styled(Box)`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledBoxHeader = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: ${props => props.theme.spacing(4)};

  & img {
    width: 100%;
    max-width: 400px;
  }
`;
const StyledBoxContent = styled(Box)`
  padding: ${props => props.theme.spacing(6)};
  padding-bottom: 0px;

  ${props => props.theme.breakpoints.down('lg')} {
    padding: 0px;
  }
`;

const TitleTypography = styled(Typography)`
  color: #fff;
  margin-bottom: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(3)};

  ${props => props.theme.breakpoints.down('lg')} {
    font-size: 28px;
  }
`;

const initialValues = {
  email: '',
  password: '',
  submit: '',
};

export type LoginFormikValues = typeof initialValues;

export enum LoginModeEnum {
  host = 'host',
  guest = 'guest',
}

interface Props {
  mode: LoginModeEnum;
}

export const LoginWrapper = ({ mode }: Props) => {
  const isGuestLogin = mode === LoginModeEnum.guest;
  const isHostLogin = mode === LoginModeEnum.host;

  // if we're logged in we don't need to login
  const userQuery = useUserQuery();
  useIsUserLoggedInRedirectToManagement(userQuery, isHostLogin);

  const theme = useTheme<any>();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { navigateToGuestApp, navigateToHostApp } = userNextRouteTransitions();

  // white-label if we're on directvacations.com page
  const isDv = IS_DIRECT_VACATIONS();
  const logo_to_display = isDv ? dvLogo : logoInvertV2;
  const companyName = isDv ? 'DirectVacations.com' : 'alluraDirect.com';
  const guest_login_title = `Save money by booking on ${companyName}`;
  const guest_login_body = `The same rental property may be listed on multiple websites. Save up to 50% on fees by booking with ${companyName} instead of airBNB or VRBO.`;
  const brandedListingPage = isDv ? dvListingPage : signUpPageFeeScreenGuestImage;

  const onSuccess = (user: User, POST_LOGIN_REDIRECT: string) => {
    if (POST_LOGIN_REDIRECT.length) {
      handleTransition('/' + POST_LOGIN_REDIRECT);
    } else {
      const hasGuestProfile = Boolean(user?.GUEST_PROFILE);
      const hasHostProfile = Boolean(user?.OWNER_PROFILE);
      const shouldNavigateToGuestApp = (isGuestLogin && hasGuestProfile) || (isHostLogin && !hasHostProfile);
      const shouldNavigateToHostApp = (isHostLogin && hasHostProfile) || (isGuestLogin && !hasGuestProfile);
      if (shouldNavigateToGuestApp) {
        navigateToGuestApp();
      } else if (shouldNavigateToHostApp) {
        navigateToHostApp();
      }
    }
  };

  const userLoginMutation = useAppUserLoginMutation({ onSuccess });

  const onSubmit = async (values: LoginFormikValues, helpers: FormikHelpers<LoginFormikValues>) => {
    userLoginMutation.mutate(
      { email: values.email, password: values.password },
      {
        onError: () => {
          helpers.setStatus({ succes: false });
          helpers.setErrors({ submit: 'There is an error with your login' });
        },
      },
    );
    helpers.setSubmitting(false);
  };

  const title = isGuestLogin ? 'Guest Login' : 'Host Login';

  return (
    <Page
      appSEOProps={{
        title: `${title}`,
        description: `Log In`,
      }}
      hasHeader={!mdUp}
      headerProps={{
        logoOnly: true,
      }}
    >
      <SplitPageLayout
        LeftColumnSlot={
          <>
            <AppLink href={httpsUrlTrailingSlash()}>
              <img src={logo_to_display} width={200} />
            </AppLink>
            <StyledBox>
              <StyledBoxHeader>
                <img src={isGuestLogin ? brandedListingPage : signUpPageFeeScreenHostImage} />
                <Typography color="white" fontSize={12}>
                  <i>{isGuestLogin ? 'Property Listing Page' : 'Rates Schedule'}</i>
                </Typography>
              </StyledBoxHeader>
              <StyledBoxContent>
                <TitleTypography variant="h4">
                  {isGuestLogin ? `${guest_login_title}` : 'Discover what similar properties are being advertised for.'}
                </TitleTypography>
                <Typography color="white">
                  {isGuestLogin
                    ? `${guest_login_body}`
                    : 'Take the guesswork out of setting nightly rates with Pricing Insights. Go to the Rate Schedule to find the marketplace averages for similar properties to yours.'}
                </Typography>
              </StyledBoxContent>
            </StyledBox>
          </>
        }
        RightColumnSlot={
          <FullPageCard step={{ title, step: 1, hasBack: false }} hasShadow={false}>
            <LoginFormikForm mode={mode} onSubmit={onSubmit} />
          </FullPageCard>
        }
      />
    </Page>
  );
};
